import type { SafeAreaInsets, Unsubscribe } from './types';

export const noopUnsubscribe: Unsubscribe = () => undefined;

export const emptyInsets: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/** Простейший диспетчер подписок для адаптеров, у которых нет своей шины событий. */
export function createEmitter<T>() {
  const handlers = new Set<(payload: T) => void>();
  return {
    subscribe(handler: (payload: T) => void): Unsubscribe {
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },
    emit(payload: T) {
      handlers.forEach((handler) => handler(payload));
    },
    get size() {
      return handlers.size;
    },
  };
}

/** Хранилище на localStorage — общий фолбэк для всех платформ. */
export function createLocalStorage(namespace: string) {
  const withKey = (key: string) => `${namespace}:${key}`;
  return {
    async get(key: string) {
      try {
        return window.localStorage.getItem(withKey(key));
      } catch {
        return null;
      }
    },
    async set(key: string, value: string) {
      try {
        window.localStorage.setItem(withKey(key), value);
      } catch {
        /* приватный режим браузера — молча пропускаем */
      }
    },
    async remove(key: string) {
      try {
        window.localStorage.removeItem(withKey(key));
      } catch {
        /* см. выше */
      }
    },
  };
}

/**
 * Скачивание файла через временную ссылку. Часть WebView блокирует программные
 * загрузки, поэтому вызывающий код обязан обрабатывать возврат false.
 */
/**
 * Запасной путь сохранения: открыть файл во внешнем браузере.
 *
 * Часть клиентов запрещает загрузку по ссылке из мини-приложения — нажатие
 * заканчивается ничем. Внешний браузер скачает или покажет файл сам, а
 * приложение при этом остаётся открытым в мессенджере.
 *
 * Работает только с публичным адресом: файлы, требующие сессию, наружу не
 * отдать, для них остаётся загрузка готового содержимого.
 */
function openInBrowser(url: string): boolean {
  if (!/^https?:\/\//.test(url)) return false;
  try {
    return window.open(url, '_blank', 'noopener,noreferrer') !== null;
  } catch {
    return false;
  }
}

/**
 * Сохранение файла средствами страницы.
 *
 * Общая часть всех трёх адаптеров: сначала обычная загрузка по ссылке, а если
 * клиент её молча запретил — открытие во внешнем браузере. Открыть можно
 * только публичный адрес: относительный путь к прокси бэкенда без cookie
 * сессии ответит отказом, и человек увидел бы пустую вкладку вместо файла.
 */
export function saveFile({
  url,
  fileName,
  blob,
}: {
  url: string;
  fileName: string;
  blob?: Blob;
}): boolean {
  const href = blob ? URL.createObjectURL(blob) : url;
  const saved = downloadViaAnchor(href, fileName);
  if (blob) setTimeout(() => URL.revokeObjectURL(href), 4000);
  if (saved) return true;
  return url.startsWith('http') ? openInBrowser(url) : false;
}

/**
 * Сохранение файла внутри мессенджера.
 *
 * Ссылка с `download` на готовый blob в WebView Telegram и MAX на телефоне
 * ничего не делает, но и не падает: нажатие «Скачать» заканчивалось тишиной.
 * Файлы ассистента при этом отдаются прокси только по cookie сессии, так что
 * нативная загрузка клиента их не получит. Остаётся системное меню «Поделиться»
 * с самим файлом — из него файл сохраняется на телефон или пересылается.
 * Там, где меню с файлами нет (WebView Android), — прежний общий путь.
 *
 * Вызывать сразу после нажатия: меню, как и нативная загрузка, требует свежего
 * действия пользователя.
 */
export async function saveFileInMessenger(payload: {
  url: string;
  fileName: string;
  blob?: Blob;
}): Promise<boolean> {
  const { blob, fileName } = payload;
  if (blob && typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
    const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: fileName });
        return true;
      } catch (error) {
        // Закрытое меню — решение человека, а не запрет клиента
        if (error instanceof DOMException && error.name === 'AbortError') return true;
      }
    }
  }
  return saveFile(payload);
}

function downloadViaAnchor(url: string, fileName: string): boolean {
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return true;
  } catch {
    return false;
  }
}

/**
 * Данные запуска из адреса страницы.
 *
 * Мессенджеры передают их по-разному: Telegram кладёт в hash-фрагмент
 * (`#tgWebAppData=...`), часть клиентов — в query. Свойство моста тоже есть не
 * всегда, поэтому адрес остаётся запасным источником: без подписанных данных
 * вход невозможен в принципе.
 *
 * Возвращается строка параметров, в которой есть подпись; иначе пустая.
 */
export function readLaunchParamsFromUrl(): string {
  if (typeof window === 'undefined') return '';

  const candidates: string[] = [];
  const hash = window.location.hash.replace(/^#/, '');
  const search = window.location.search.replace(/^\?/, '');

  for (const raw of [hash, search]) {
    if (!raw) continue;
    candidates.push(raw);

    // Данные лежат вложенно: `tgWebAppData=<строка>` у Telegram и
    // `WebAppData=<строка>` у MAX — подпись внутри этого значения
    for (const [, value] of new URLSearchParams(raw).entries()) {
      if (hasLaunchSignature(value)) candidates.push(value);
    }
  }

  return candidates.find(hasLaunchSignature) ?? '';
}

/**
 * Имена, под которыми клиенты присылают подпись данных запуска.
 *
 * Telegram называет её `hash`; другие клиенты используют свои имена, поэтому
 * список открытый — иначе подписанные данные считались бы отсутствующими.
 */
const SIGNATURE_KEYS = ['hash', 'sign', 'signature'];

/** Есть ли в данных запуска подпись: без неё бэкенд их не примет. */
export function hasLaunchSignature(initData: string): boolean {
  if (!initData) return false;
  const params = new URLSearchParams(initData);
  return SIGNATURE_KEYS.some((key) => (params.get(key) ?? '').length > 0);
}

/**
 * Кто открыл приложение, по данным запуска в адресе.
 *
 * Telegram кладёт их под ключами с приставкой `tg` (`tgWebAppData`), MAX —
 * под теми же именами без неё (`WebAppData`). Это единственный надёжный
 * признак: MAX не выставляет на странице своего объекта, а SDK Telegram на
 * странице есть всегда — он подключён разметкой и в чужом клиенте отдаёт
 * пустышку.
 *
 * Ошибиться здесь нельзя: платформа уходит на сервер, и подпись проверяется
 * ключом того бота, который её поставил.
 */
export function detectLaunchPlatform(): 'telegram' | 'max' | null {
  if (typeof window === 'undefined') return null;

  const raw = `${window.location.hash.replace(/^#/, '')}&${window.location.search.replace(/^\?/, '')}`;
  const keys = [...new URLSearchParams(raw).keys()];

  if (keys.some((key) => key.startsWith('tgWebApp'))) return 'telegram';
  if (keys.some((key) => key.startsWith('WebApp'))) return 'max';
  return null;
}
